import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Coupon, StorePromotionsPayload, PromoBannerConfig, HeroOfferConfig } from '@vastra/types';
import { DEFAULT_STORE_PROMOTIONS } from '@vastra/constants';
import {
  isCouponCurrentlyValid,
  isPromoWindowActive,
  normalizePromoBanners,
  selectActivePromoBanners,
} from '@vastra/utils';
import { api } from '../services/api';

interface PromotionsContextValue {
  loading: boolean;
  payload: StorePromotionsPayload;
  /** Coupons valid for checkout (active + not expired). */
  checkoutCoupons: Coupon[];
  /**
   * @deprecated Prefer `activeBanners`. First live banner (compat).
   */
  activeBanner: PromoBannerConfig | null;
  /** Live banners for the storefront (max 1–2 per publish settings). */
  activeBanners: PromoBannerConfig[];
  /** Whether home should render the offer hero. */
  showOfferHero: boolean;
  hero: HeroOfferConfig;
}

const PromotionsContext = createContext<PromotionsContextValue | null>(null);

export function usePromotions() {
  const ctx = useContext(PromotionsContext);
  if (!ctx) throw new Error('usePromotions must be used within PromotionsProvider');
  return ctx;
}

export function PromotionsProvider({ children }: { children: React.ReactNode }) {
  const [payload, setPayload] = useState<StorePromotionsPayload>(DEFAULT_STORE_PROMOTIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .loadPromotions()
      .then((p) => {
        if (!cancelled) setPayload(p);
      })
      .catch(() => {
        if (!cancelled) setPayload(DEFAULT_STORE_PROMOTIONS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const checkoutCoupons = useMemo(() => {
    const list = Array.isArray(payload.coupons) ? payload.coupons : [];
    return list.filter(isCouponCurrentlyValid);
  }, [payload.coupons]);

  const activeBanners = useMemo((): PromoBannerConfig[] => {
    const list = normalizePromoBanners(payload);
    return selectActivePromoBanners(list, payload.maxActiveBanners ?? 2);
  }, [payload]);

  const activeBanner = activeBanners[0] ?? null;

  const hero = payload.hero ?? DEFAULT_STORE_PROMOTIONS.hero!;

  const showOfferHero = useMemo(() => {
    if (!hero.enabled || hero.mode !== 'offer') return false;
    if (!hero.headline?.trim()) return false;
    return isPromoWindowActive(hero.startsAt, hero.endsAt);
  }, [hero]);

  const value: PromotionsContextValue = {
    loading,
    payload,
    checkoutCoupons,
    activeBanner,
    activeBanners,
    showOfferHero,
    hero,
  };

  return <PromotionsContext.Provider value={value}>{children}</PromotionsContext.Provider>;
}
